/**
 * Friendly route-level error boundary for `react-router-dom`'s
 * `createBrowserRouter`. Pass it as the root route's `errorElement` and
 * every render-time throw inside that route subtree lands here instead of
 * react-router's default "Unexpected Application Error!" stack-trace
 * screen.
 *
 * Three flavors of error, three flavors of response:
 *
 *   1. `MissingActiveContextError` -- the operator app's workspace
 *      selection got wiped (iOS in-app browser, cross-tab clear, private
 *      mode storage failure). We redirect to `/choose-context` silently;
 *      this is an expected recovery, not a crash, and the user shouldn't
 *      see any "something went wrong" framing.
 *
 *   2. `ErrorResponse` from react-router (`throw new Response(...)` or
 *      `<Route loader>` 4xx/5xx). We map common statuses to a friendly
 *      page-not-found / unauthorized message without reporting.
 *
 *   3. Anything else -- a genuine render throw. We fire `reportError` to
 *      mint a PCE-XXXXXXXX reference code and show `<ErrorFallback>`. The
 *      UI is identical to what `<GlobalErrorBoundary>` shows for
 *      provider-level crashes, so the experience is consistent regardless
 *      of where the failure happens.
 *
 * Why a separate boundary at the router level: react-router catches
 * route-render throws *internally* and never bubbles them up to React's
 * normal error-boundary chain. The outer `<GlobalErrorBoundary>` only
 * sees throws outside the router (providers, etc.). Without this
 * element, route throws hit react-router's dev-style default UI -- the
 * one that leaks the stack to end users.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Navigate,
  isRouteErrorResponse,
  useRouteError,
  type ErrorResponse,
} from 'react-router-dom';
import { isMissingActiveContextError, reportError } from '@pc/core';
import { Button } from './Button';
import { Card } from './Card';
import { Display } from './Display';
import { SmallCap } from './SmallCap';
import { ErrorFallback } from './ErrorFallback';
import { isChunkLoadError, scheduleChunkReload } from './lazyWithReload';

export interface RouterErrorElementProps {
  /** Where to send the user from missing-context redirects. Defaults to
   *  `/choose-context` to match the operator app's convention. */
  chooseContextHref?: string;
  /** "Back to home" href. Default `/`. */
  homeHref?: string;
  /** Support inbox. Default `support@peptideclients.com`. */
  supportEmail?: string;
}

type ResolvedError =
  | { kind: 'missing-context'; href: string }
  | { kind: 'route-response'; response: ErrorResponse }
  /**
   * Stale code-split chunk: the user is on an old shell after a
   * deploy and a `React.lazy(() => import(...))` 404'd. The right
   * answer is a silent hard reload to pick up the new index.html,
   * not a "something went wrong" screen. We render a brief loading
   * card while the reload fires. If the reload debounce trips
   * (e.g. the new deploy is also broken) we fall through to `crash`
   * so the user lands on the friendly fallback with a reference
   * code instead of a refresh loop.
   */
  | { kind: 'stale-chunk' }
  | { kind: 'crash'; error: Error };

function resolve(err: unknown, chooseContextHref: string): ResolvedError {
  if (isMissingActiveContextError(err)) {
    return { kind: 'missing-context', href: chooseContextHref };
  }
  if (isRouteErrorResponse(err)) {
    return { kind: 'route-response', response: err };
  }
  if (isChunkLoadError(err)) {
    return { kind: 'stale-chunk' };
  }
  if (err instanceof Error) {
    return { kind: 'crash', error: err };
  }
  return {
    kind: 'crash',
    error: new Error(typeof err === 'string' ? err : 'Unknown route error'),
  };
}

export function RouterErrorElement({
  chooseContextHref = '/choose-context',
  homeHref = '/',
  supportEmail,
}: RouterErrorElementProps) {
  const err = useRouteError();
  const resolved = useMemo(() => resolve(err, chooseContextHref), [err, chooseContextHref]);

  if (resolved.kind === 'missing-context') {
    return <Navigate to={resolved.href} replace />;
  }
  if (resolved.kind === 'route-response') {
    return (
      <RouteResponseScreen
        response={resolved.response}
        homeHref={homeHref}
        supportEmail={supportEmail}
      />
    );
  }
  if (resolved.kind === 'stale-chunk') {
    return <StaleChunkRecoveryScreen homeHref={homeHref} supportEmail={supportEmail} />;
  }
  return <CrashScreen error={resolved.error} homeHref={homeHref} supportEmail={supportEmail} />;
}

/**
 * Fires a single auto-reload (debounced via sessionStorage) and shows
 * a minimal "updating..." card during the ~100ms before the navigation
 * commits. If the debounce trips we render the CrashScreen instead so
 * the user isn't stranded in an apparent loading-forever state.
 */
function StaleChunkRecoveryScreen({
  homeHref,
  supportEmail,
}: {
  homeHref: string;
  supportEmail?: string;
}) {
  const [debounceTripped, setDebounceTripped] = useState(false);

  useEffect(() => {
    const reloading = scheduleChunkReload();
    if (!reloading) setDebounceTripped(true);
  }, []);

  if (debounceTripped) {
    // The new build is *also* failing to load this chunk — fall
    // through to the branded error screen with a real ref code so
    // support can dig in instead of looping the user.
    return (
      <CrashScreen
        error={
          new Error(
            'Failed to load the latest app build after auto-reload. The deploy may be in flight.',
          )
        }
        homeHref={homeHref}
        supportEmail={supportEmail}
      />
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-paper px-6 py-12">
      <div className="w-full max-w-md flex flex-col items-center gap-3 text-center">
        <span
          aria-hidden="true"
          className="h-6 w-6 rounded-full border-2 border-ink/20 border-t-ink animate-spin"
        />
        <p className="display text-xl">{'Updating to the latest version\u2026'}</p>
        <p className="text-sm text-muted">
          We just shipped a new build. One moment.
        </p>
      </div>
    </div>
  );
}

/**
 * Friendly mapper for `throw new Response(...)` and route-loader-thrown
 * 4xx/5xx. Most apps won't hit this path, but keeping a branded screen
 * here means a 404 deep link or a 401 from a loader never falls back to
 * react-router's default.
 */
function RouteResponseScreen({
  response,
  homeHref,
  supportEmail,
}: {
  response: ErrorResponse;
  homeHref: string;
  supportEmail?: string;
}) {
  const { status } = response;
  const meta = describeStatus(status);

  return (
    <div className="min-h-screen grid place-items-center bg-paper px-6 py-12">
      <div className="w-full max-w-xl flex flex-col gap-6">
        <SmallCap>{meta.eyebrow}</SmallCap>
        <Display size="lg">{meta.title}</Display>
        <p className="text-base text-ink-2 max-w-prose leading-relaxed">{meta.body}</p>

        <div className="flex flex-wrap gap-3">
          <Button
            variant="primary"
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined') window.location.assign(homeHref);
            }}
          >
            Back to home
          </Button>
          <Button
            variant="ghost"
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined') window.history.back();
            }}
          >
            Go back
          </Button>
        </div>

        {supportEmail ? (
          <Card>
            <p className="text-sm text-muted">
              Still stuck? Email{' '}
              <a href={`mailto:${supportEmail}`} className="underline hover:text-ink break-all">
                {supportEmail}
              </a>
              .
            </p>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

interface StatusMeta {
  eyebrow: string;
  title: ReactNode;
  body: string;
}

function describeStatus(status: number): StatusMeta {
  if (status === 404) {
    return {
      eyebrow: 'Page not found',
      title: (
        <>
          We can&rsquo;t find that <em>page</em>.
        </>
      ),
      body:
        'The link may be out of date, or the page has been moved or removed. ' +
        'Head back to the dashboard to keep going.',
    };
  }
  if (status === 401 || status === 403) {
    return {
      eyebrow: 'Access restricted',
      title: (
        <>
          You don&rsquo;t have <em>access</em> to this page.
        </>
      ),
      body:
        'Your account is signed in, but this area is reserved for users with different ' +
        'permissions. If you think this is a mistake, ask a workspace owner to grant access.',
    };
  }
  if (status >= 500) {
    return {
      eyebrow: 'Service unavailable',
      title: (
        <>
          We&rsquo;re having trouble loading <em>this page</em>.
        </>
      ),
      body:
        'Our servers returned an error. The issue has been logged automatically; ' +
        'try again in a moment, or head back to the dashboard.',
    };
  }
  return {
    eyebrow: `Error ${status}`,
    title: (
      <>
        Something <em>went wrong</em>.
      </>
    ),
    body:
      'We couldn\u2019t complete this request. Head back to the dashboard, or try again in a moment.',
  };
}

/**
 * Generic render-time crash: report it through the global errors pipeline
 * (same one `<GlobalErrorBoundary>` uses), wait for the PCE-XXXXXXXX id,
 * then render the branded `<ErrorFallback>`. Pending state shows the
 * fallback immediately with a placeholder code so the user never stares at
 * a white screen while the network call lands.
 */
function CrashScreen({
  error,
  homeHref,
  supportEmail,
}: {
  error: Error;
  homeHref: string;
  supportEmail?: string;
}) {
  const [shortCode, setShortCode] = useState<string>('PCE-…');

  useEffect(() => {
    let alive = true;
    reportError(error, {
      severity: 'fatal',
      tags: { source: 'react_router.error_element' },
    })
      .then((res) => {
        if (alive) setShortCode(res.short_code);
      })
      .catch(() => {
        if (alive) setShortCode('PCE-FAILED00');
      });
    return () => {
      alive = false;
    };
  }, [error]);

  const details = [error.message, error.stack ? '' : null, error.stack ?? null]
    .filter((v): v is string => typeof v === 'string')
    .join('\n');

  return (
    <ErrorFallback
      shortCode={shortCode}
      homeHref={homeHref}
      supportEmail={supportEmail}
      technicalDetails={details || null}
      hint={
        shortCode === 'PCE-…'
          ? 'Sending the error report\u2026'
          : shortCode === 'PCE-FAILED00'
            ? 'We could not reach our servers; please include the page URL and a screenshot in your email.'
            : 'A copy was sent to our team automatically.'
      }
    />
  );
}
